/**
 * ImageUploader — drag-and-drop or click-to-upload image field.
 *
 * Posts the chosen file to `/api/cms/media/` (admin-gated on the backend)
 * and calls `onUploaded(url)` with the resulting absolute URL so parent
 * forms can store it in the usual `thumbnail_url` / `image_url` text field.
 *
 * Falls back to a plain text URL input so admins can paste a CDN or
 * Unsplash link directly — the uploader is an *addition* to, not a
 * replacement of, the existing URL field.
 */
import { useRef, useState } from 'react';
import { Upload, Loader2, X } from 'lucide-react';
import { Input } from './input';
import { Label } from './label';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

export default function ImageUploader({
  value = '',
  onChange,
  folder = 'uploads',
  label = 'Image URL',
  testidPrefix = 'image-uploader',
}) {
  const { api } = useAuth();
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type?.startsWith('image/')) {
      toast.error('Only image files are allowed');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be under 10 MB');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('name', file.name);
      formData.append('folder', folder);
      const { data } = await api.post('/cms/media/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      // MediaAsset serializer returns either `file` (relative) or `url`.
      // Use whichever is non-empty.
      const url = data.url || data.file_url || (data.file
        ? (data.file.startsWith('http') ? data.file : `${window.location.origin}${data.file}`)
        : '');
      if (!url) {
        toast.error('Upload succeeded but no URL returned');
        return;
      }
      onChange(url);
      toast.success('Image uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div data-testid={`${testidPrefix}-wrapper`}>
      <Label>{label}</Label>

      {/* URL text input (still the source of truth — uploader just fills it in) */}
      <Input
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder="https://… or drop an image below"
        data-testid={`${testidPrefix}-url-input`}
      />

      {/* Drag/drop + click zone */}
      <div
        className={`mt-2 border-2 border-dashed rounded-lg p-4 text-center text-xs cursor-pointer transition-colors ${
          dragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'
        }`}
        onClick={() => inputRef.current?.click()}
        onDragOver={e => { e.preventDefault(); setDragActive(true); }}
        onDragLeave={() => setDragActive(false)}
        onDrop={e => {
          e.preventDefault();
          setDragActive(false);
          const f = e.dataTransfer?.files?.[0];
          if (f) handleFile(f);
        }}
        data-testid={`${testidPrefix}-dropzone`}
      >
        {uploading ? (
          <div className="flex items-center justify-center gap-2 text-blue-600">
            <Loader2 className="h-4 w-4 animate-spin" /> Uploading…
          </div>
        ) : (
          <div className="flex flex-col items-center gap-1 text-gray-600">
            <Upload className="h-5 w-5" />
            <span>Drop an image here, or click to browse</span>
            <span className="text-[10px] text-gray-400">PNG / JPG / WEBP · 10 MB max</span>
          </div>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={e => handleFile(e.target.files?.[0])}
        data-testid={`${testidPrefix}-file-input`}
      />

      {/* Inline preview + clear */}
      {value && !uploading && (
        <div className="mt-2 relative inline-block">
          <img
            src={value}
            alt="preview"
            className="h-24 w-auto rounded border"
            onError={e => { e.target.style.display = 'none'; }}
          />
          <button
            type="button"
            onClick={() => onChange('')}
            className="absolute -top-2 -right-2 bg-white border rounded-full p-0.5 shadow hover:bg-red-50"
            title="Clear image"
            data-testid={`${testidPrefix}-clear-btn`}
          >
            <X className="h-3 w-3 text-gray-500" />
          </button>
        </div>
      )}
    </div>
  );
}
